import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { TenantProvider } from "@/contexts/TenantContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";

import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import SpectatorView from "@/pages/SpectatorView";
import UmpirePanel from "@/pages/UmpirePanel";
import SetBasedScoring from "@/pages/SetBasedScoring";
import MatchesList from "@/pages/MatchesList";
import AuctionView from "@/pages/AuctionView";
import PlayersPage from "@/pages/PlayersPage";
import PlayerProfile from "@/pages/PlayerProfile";
import TeamsPage from "@/pages/TeamsPage";
import AdminConfig from "@/pages/AdminConfig";
import Operations from "@/pages/Operations";
import Jerseys from "@/pages/Jerseys";
import PrizePool from "@/pages/PrizePool";
import PublicLive from "@/pages/PublicLive";
import PublicMatch from "@/pages/PublicMatch";
import PublicHome from "@/pages/PublicHome";
import RegisterPlayer from "@/pages/RegisterPlayer";
import Signup from "@/pages/Signup";
import UsersManagement from "@/pages/UsersManagement";
import CMS from "@/pages/CMS";
import PublicTeam from "@/pages/PublicTeam";
import TeamBrandingEdit from "@/pages/TeamBrandingEdit";
import PlayersDirectory from "@/pages/PlayersDirectory";
import PublicStandings from "@/pages/PublicStandings";
import AuditLogs from "@/pages/AuditLogs";
import Exports from "@/pages/Exports";
import RolePermissions from "@/pages/RolePermissions";
import Registrations from "@/pages/Registrations";
import Schedule from "@/pages/Schedule";
import Bracket from "@/pages/Bracket";
import Meetings from "@/pages/Meetings";
import SponsorInquiries from "@/pages/SponsorInquiries";
import TenantsPage from "@/pages/TenantsPage";
import OwnerCommandCenter from "@/pages/OwnerCommandCenter";
import BroadcastOverlay from "@/pages/BroadcastOverlay";
import BroadcastStudio from "@/pages/BroadcastStudio";
import EventLog from "@/pages/EventLog";
import NotificationsPage from "@/pages/NotificationsPage";
import RefereePortal from "@/pages/RefereePortal";
import PlayerPortal from "@/pages/PlayerPortal";
import PublicDisplay from "@/pages/PublicDisplay";
import DisplayController from "@/pages/DisplayController";
import Season3Import from "@/pages/Season3Import";
import TeamProfile from "@/pages/TeamProfile";
import GroupsAdmin from "@/pages/GroupsAdmin";
import TeamOwnersAdmin from "@/pages/TeamOwnersAdmin";
import TeamCorrections from "@/pages/TeamCorrections";
import MatchFormats from "@/pages/MatchFormats";
import CourtBlocksAdmin from "@/pages/CourtBlocksAdmin";
import TieView from "@/pages/TieView";
import TeamDetails from "@/pages/TeamDetails";
import MatchDetails from "@/pages/MatchDetails";
import KnockoutBracket from "@/pages/KnockoutBracket";
import EmbedWidget from "@/pages/EmbedWidget";
import QFQualification from "@/pages/QFQualification";
import QFDrawReveal from "@/pages/QFDrawReveal";
import OwnerLineup from "@/pages/OwnerLineup";
import Broadcast from "@/pages/Broadcast";
import WildCardStory from "@/pages/WildCardStory";
import SimulationDashboard from "@/pages/SimulationDashboard";
import SeedingAdmin from "@/pages/SeedingAdmin";
import TVScoreboard from "@/pages/TVScoreboard";
import RefereeCourt from "@/pages/RefereeCourt";
import SplashRedirect from "@/pages/SplashRedirect";

function AppRoutes() {
  return (
    <Routes>
      {/* Public marketing home page */}
      <Route path="/" element={<PublicHome />} />
      {/* Operator / admin sign-in (4 role tabs) */}
      <Route path="/admin" element={<Login />} />
      {/* Legacy /login redirects to /admin */}
      <Route path="/login" element={<Navigate to="/admin" replace />} />
      {/* Live broadcast feed (kept as-is for spectators following matches) */}
      <Route path="/live" element={<PublicLive />} />
      <Route path="/tv" element={<TVScoreboard />} />
      <Route path="/tv/scoreboard" element={<TVScoreboard />} />
      <Route path="/watch" element={<PublicMatch />} />
      <Route path="/watch/:matchId" element={<PublicMatch />} />
      <Route path="/tie/:tieId" element={<TieView />} />
      <Route path="/register-player" element={<RegisterPlayer />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/team/:slug" element={<PublicTeam />} />
      <Route path="/team/:slug/edit" element={<ProtectedRoute><TeamBrandingEdit /></ProtectedRoute>} />
      {/* Comprehensive Team Details — single source of truth, public route */}
      <Route path="/teams/:teamId" element={<TeamDetails />} />
      {/* Individual Match — big scoreboard + live per-point ticker */}
      <Route path="/matches/:matchId" element={<MatchDetails />} />
      {/* Public Player Profile — career stats via /api/public/player/{id} */}
      <Route path="/players/:id" element={<PlayerProfile />} />
      <Route path="/players-directory" element={<PlayersDirectory />} />
      <Route path="/qf-reveal" element={<QFDrawReveal />} />
      <Route path="/broadcast" element={<Broadcast />} />
      <Route path="/wildcard-story" element={<WildCardStory />} />
      <Route path="/splash/:size" element={<SplashRedirect />} />
      <Route path="/standings" element={<PublicStandings />} />
      {/* Broadcast overlay — public, no auth, chroma/transparent bg for OBS/vMix */}
      <Route path="/overlay/:matchId" element={<BroadcastOverlay />} />
      {/* Public Display — no-auth full-screen venue screen (Season 3 Team Portal §14-§26) */}
      <Route path="/display" element={<PublicDisplay />} />
      <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/spectator" element={<SpectatorView />} />
        <Route path="/spectator/:matchId" element={<SpectatorView />} />
        <Route path="/auction" element={<AuctionView />} />
        <Route path="/players" element={<PlayersPage />} />
        <Route path="/teams" element={<TeamsPage />} />
        <Route path="/prize-pool" element={<PrizePool />} />
        <Route path="/jerseys" element={<Jerseys />} />
        <Route path="/schedule" element={<Schedule />} />
        <Route path="/bracket" element={<Bracket />} />
        <Route path="/meetings" element={
          <ProtectedRoute roles={["super_admin", "league_admin", "owner", "court_umpire"]}><Meetings /></ProtectedRoute>
        } />
        <Route path="/sponsor-inquiries" element={
          <ProtectedRoute roles={["super_admin", "league_admin"]}><SponsorInquiries /></ProtectedRoute>
        } />

        <Route path="/matches" element={
          <ProtectedRoute roles={["super_admin", "league_admin", "court_umpire"]}><MatchesList /></ProtectedRoute>
        } />
        <Route path="/umpire/:matchId" element={
          <ProtectedRoute roles={["super_admin", "league_admin", "court_umpire"]}><UmpirePanel /></ProtectedRoute>
        } />
        <Route path="/scoring/:matchId" element={
          <ProtectedRoute roles={["super_admin", "league_admin", "court_umpire"]}><SetBasedScoring /></ProtectedRoute>
        } />
        <Route path="/registrations" element={
          <ProtectedRoute roles={["super_admin", "league_admin"]}><Registrations /></ProtectedRoute>
        } />
        <Route path="/operations" element={
          <ProtectedRoute roles={["super_admin", "league_admin"]}><Operations /></ProtectedRoute>
        } />
        <Route path="/admin-config" element={
          <ProtectedRoute roles={["super_admin", "league_admin"]}><AdminConfig /></ProtectedRoute>
        } />
        <Route path="/users" element={
          <ProtectedRoute roles={["super_admin"]}><UsersManagement /></ProtectedRoute>
        } />
        <Route path="/cms" element={
          <ProtectedRoute roles={["super_admin", "league_admin"]}><CMS /></ProtectedRoute>
        } />
        <Route path="/audit-logs" element={
          <ProtectedRoute roles={["super_admin"]}><AuditLogs /></ProtectedRoute>
        } />
        <Route path="/exports" element={
          <ProtectedRoute roles={["super_admin", "league_admin"]}><Exports /></ProtectedRoute>
        } />
        <Route path="/role-permissions" element={
          <ProtectedRoute roles={["super_admin"]}><RolePermissions /></ProtectedRoute>
        } />
        <Route path="/tenants" element={
          <ProtectedRoute roles={["super_admin"]}><TenantsPage /></ProtectedRoute>
        } />
        <Route path="/owner" element={
          <ProtectedRoute roles={["owner", "super_admin", "league_admin"]}><OwnerCommandCenter /></ProtectedRoute>
        } />
        <Route path="/broadcast-studio" element={
          <ProtectedRoute roles={["super_admin", "league_admin", "court_umpire"]}><BroadcastStudio /></ProtectedRoute>
        } />
        <Route path="/events" element={
          <ProtectedRoute roles={["super_admin", "league_admin"]}><EventLog /></ProtectedRoute>
        } />
        <Route path="/notifications" element={
          <ProtectedRoute><NotificationsPage /></ProtectedRoute>
        } />
        <Route path="/referee" element={
          <ProtectedRoute roles={["court_umpire", "super_admin", "league_admin"]}><RefereePortal /></ProtectedRoute>
        } />
        <Route path="/referee/court/:court" element={
          <ProtectedRoute roles={["court_umpire", "super_admin", "league_admin"]}><RefereeCourt /></ProtectedRoute>
        } />
        <Route path="/player" element={
          <ProtectedRoute><PlayerPortal /></ProtectedRoute>
        } />
        <Route path="/admin/display" element={
          <ProtectedRoute roles={["super_admin", "league_admin"]}><DisplayController /></ProtectedRoute>
        } />
        <Route path="/admin/season3-import" element={
          <ProtectedRoute roles={["super_admin", "league_admin"]}><Season3Import /></ProtectedRoute>
        } />
        <Route path="/team/profile" element={
          <ProtectedRoute roles={["owner", "super_admin", "league_admin"]}><TeamProfile /></ProtectedRoute>
        } />
        <Route path="/admin/groups" element={
          <ProtectedRoute roles={["super_admin", "league_admin"]}><GroupsAdmin /></ProtectedRoute>
        } />
        <Route path="/admin/team-owners" element={
          <ProtectedRoute roles={["super_admin", "league_admin"]}><TeamOwnersAdmin /></ProtectedRoute>
        } />
        <Route path="/admin/team-corrections" element={
          <ProtectedRoute roles={["super_admin", "league_admin"]}><TeamCorrections /></ProtectedRoute>
        } />
        <Route path="/admin/match-formats" element={
          <ProtectedRoute roles={["super_admin", "league_admin"]}><MatchFormats /></ProtectedRoute>
        } />
        <Route path="/admin/court-blocks" element={
          <ProtectedRoute roles={["super_admin", "league_admin"]}><CourtBlocksAdmin /></ProtectedRoute>
        } />
        <Route path="/admin/knockouts" element={
          <ProtectedRoute roles={["super_admin", "league_admin"]}><KnockoutBracket /></ProtectedRoute>
        } />
        <Route path="/admin/widget" element={
          <ProtectedRoute roles={["super_admin", "league_admin"]}><EmbedWidget /></ProtectedRoute>
        } />
        <Route path="/admin/qf-qualification" element={
          <ProtectedRoute roles={["super_admin", "league_admin"]}><QFQualification /></ProtectedRoute>
        } />
        <Route path="/admin/simulation" element={
          <ProtectedRoute roles={["super_admin"]}><SimulationDashboard /></ProtectedRoute>
        } />
        <Route path="/admin/seeding" element={
          <ProtectedRoute roles={["super_admin", "league_admin"]}><SeedingAdmin /></ProtectedRoute>
        } />
        <Route path="/owner/lineups/:tie_id" element={
          <ProtectedRoute roles={["owner", "super_admin", "league_admin"]}><OwnerLineup /></ProtectedRoute>
        } />
        <Route path="/my-team" element={
          <ProtectedRoute roles={["owner", "super_admin", "league_admin"]}><TeamProfile /></ProtectedRoute>
        } />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <TenantProvider>
        <BrowserRouter>
          <AppRoutes />
          <PWAInstallPrompt />
          <Toaster theme="dark" position="top-right" richColors />
        </BrowserRouter>
      </TenantProvider>
    </AuthProvider>
  );
}
